import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { getAgentOptions } from "@/api";
import { EntityDetailPage } from "@/components/build/EntityDetailPage";
import { EditWithAiButton } from "@/features/build/withAi/EditWithAiButton";
import { AgentAccessTab } from "./AgentAccessTab";
import { AgentCapabilitiesTab } from "./AgentCapabilitiesTab";
import { AgentForm } from "./AgentForm";
import { useDeleteAgent } from "./useDeleteAgent";

/**
 * Build > Agents > detail (docs/design/web/WEB_BUILD_PATTERN_PLAN.md §2): the shared
 * EntityDetailPage scaffold; Overview (form), Capabilities, Access tabs.
 */
export function AgentDetailPage() {
  const { t } = useTranslation(["build", "agents"]);
  const navigate = useNavigate();
  const { uuid = "" } = useParams();
  const agent = useQuery(getAgentOptions({ path: { agent_uuid: uuid } }));
  const { onDelete, dialog } = useDeleteAgent(() => navigate("/build/agents"));
  const data = agent.data?.data;

  return (
    <EntityDetailPage
      listPath="/build/agents"
      rootLabel={t("agents:title")}
      name={data?.name ?? ""}
      isPending={agent.isPending}
      isError={agent.isError}
      notFoundLabel={t("agents:detail.notFound")}
      tabs={[
        { value: "overview", label: t("agents:tabs.overview"), content: data && <AgentForm agent={data} /> },
        {
          value: "capabilities",
          label: t("agents:tabs.capabilities"),
          content: data && <AgentCapabilitiesTab agent={data} />,
        },
        { value: "access", label: t("agents:tabs.access"), content: data && <AgentAccessTab agent={data} /> },
      ]}
      canDelete={data?.canManage ?? false}
      onDelete={() => data && onDelete(data)}
      deleteDialog={dialog}
      extraActions={data?.canEdit ? <EditWithAiButton entityType="agent" uuid={data.uuid} /> : undefined}
    />
  );
}
